import List from "@mui/material/List";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as ChevronLeftIcon } from "components/Icons/chevron-left.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useLocation, useNavigate } from "react-router-dom";
import { SubMenu } from "types/sidebar";
import { links as linksList } from "utilities/links";
import { Link } from "./Link";
import { isGlobalManagementView, isSingleMemberView } from "./utils";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    headerWrapper: {
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      "& svg": {
        color: theme.gray500,
        height: "20px",
      },

      "&:hover": {
        "& svg,p": {
          color: theme.black,
        },
      },
    },
    headerWrapperClosed: {
      width: "48px",
      height: "48px",
      justifyContent: "center",
      paddingTop: ({ isDrawerOpen }: { isDrawerOpen?: boolean }) =>
        !isDrawerOpen ? theme.spacing(4) : 0,
    },
    header: {
      color: "#808080",
      fontSize: 14,
      fontWeight: 400,

      "&:hover": {
        color: theme.black,
      },
    },
    list: {
      marginTop: theme.spacing(6),
      padding: theme.spacing(0, 8, 8),
    },
    container: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      marginTop: theme.spacing(8),
    },
    closedList: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(5),
      marginTop: 0,
    },
    listItem: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(3, 8),
      gap: theme.spacing(8),
      borderRadius: "6px",
      textDecoration: "none",
      color: "#6D6D6D",
      cursor: "pointer",

      "&:active": {
        color: "#6D6D6D",
      },

      "& svg": {
        stroke: theme.primary200,
        color: theme.primary200,
        fill: theme.primary200,
      },
    },
  }),
);

interface Props extends SubMenu {
  closeMenu: () => void;
  isDrawerOpen: boolean;
}

export const SubMenuList = ({
  header,
  links,
  closeMenu,
  isDrawerOpen,
}: Props) => {
  const classes = useStyles({ isDrawerOpen });
  const { message } = useMessage();
  const location = useLocation();
  const navigate = useNavigate();

  const handleCloseButton = () => {
    isGlobalManagementView(location.pathname) && navigate(linksList.dashboard);
    isSingleMemberView(location.pathname) && navigate(linksList.membersList);
    closeMenu();
  };

  return (
    <List
      className={`${classes.list} ${!isDrawerOpen ? classes.closedList : ""}`}
    >
      <div
        onClick={handleCloseButton}
        className={`${!isDrawerOpen ? classes.headerWrapperClosed : ""} ${classes.headerWrapper}`}
        data-test-id="sidebar-close-submenu"
      >
        <ChevronLeftIcon />
        {isDrawerOpen && (
          <div className={classes.header} data-sentry-unmask>
            {typeof header === "string" ? message({ id: header }) : header}
          </div>
        )}
      </div>
      <div
        className={`${classes.container} ${!isDrawerOpen ? classes.closedList : ""}`}
      >
        {links.map((linkDetail, index) => (
          <Link key={index} details={linkDetail} isDrawerOpen={isDrawerOpen} />
        ))}
      </div>
    </List>
  );
};
